import { useDropzone } from 'react-dropzone';
import { useCallback, useEffect, useState } from 'react';
import { Button, Dialog, Popover, Tooltip } from '@material-ui/core';
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { cloneDeep } from 'lodash';
import { PropTypes } from 'prop-types';
import ButtonOption from '@/component/common/ButtonOption';
import trI18n from '@/common/utils/trI18n';
import TranslationConstant from '@/common/constant/TranslationConstant';
import useTranslationSubscription from '@/hooks/useTranslation';
import AttachFileThumbnail from '@/component/common/AttachFileThumbnail/components/AttachFileThumbnail';
import useAttachThumbnailSize from '@/component/common/AttachFileThumbnail/handler/useAttachThumbnailSize';
import { THUMBNAIL_SIZE_CLIENT_CONTROL } from '@/component/common/icons/AttachUtils';
import '@/component/common/AttachFileThumbnail/components/AttachThumbnailList.css';

export const DropFile = ({ fileUploadList, setFileUploadList, setValue, register, name, ...props }) => {
    useTranslationSubscription();
    register(name);

    const [publicForCustomer, setPublicForCustomer] = useState(0);
    const [isDisable, setIsDisable] = useState(false);
    const isUploadForEmailTicket = props.selectTicketType === Constant.HELPDESK_CREATE_TICKET_TYPE.EMAIL || props.ticketType === Constant.TICKET_TYPE.EMAIL;
    const [openPreviewImage, setOpenPreviewImage] = useState(null);
    const [openOption, setOpenOption] = useState(null);
    const [openFileOption, setOpenFileOption] = useState(null);
    const thumbnailSize = useAttachThumbnailSize(THUMBNAIL_SIZE_CLIENT_CONTROL.TICKET_DETAIL);

    useEffect(() => {
        setPublicForCustomer(props.data.structure.publicForCustomer);

        setIsDisable(checkDisableField());

    }, [props.data.structure.publicForCustomer, props.detailTicket, props.hasEditFieldPermissions])

    const onDrop = useCallback(
        (acceptedFiles) => {
            handleDragFileOnChange(acceptedFiles);
            return;
        },
        // CR-2026-006: the attachment field keeps its key when the format changes, so the
        // closure must refresh with the new format's autoTranslateAttachment (BR-12 default ON).
        [fileUploadList, props?.autoTranslateAttachment]
    );
    const { getRootProps, getInputProps } = useDropzone({ onDrop, disabled: isDisable });

    const handleDragFileOnChange = (fileList) => {
        let tempFileUploadList = [];
        for (let i = 0; i < fileList.length; i++){
            if (fileList[i]) {
                let fileUpload = { file: fileList[i], progress: 0, status: true, key: '', autoTranslate: Boolean(props?.autoTranslateAttachment)};
                setValue(name, 'fileUploaded');
                tempFileUploadList.push(fileUpload);
            }
            let newFileUploadList = [...fileUploadList, ...tempFileUploadList];
            setFileUploadList(newFileUploadList);
            setValue(Constant.FORM_FILE_UPLOAD_LIST_INPUT_NAME, newFileUploadList);
        }

        if(!Utils.isEmptyArray(tempFileUploadList)) {
            props.clearErrors(name);
            setValue(name, 'fileUploaded');
            props?.setIsEdited(true);
            props?.setShowButtonEditTicket && props?.setShowButtonEditTicket();
            if(props?.setOnChangeEdit){
                if(props?.setOnChangeEdit){
                    props?.setOnChangeEdit(!props?.onChangeEdit)
                }
            }
        } else {
            setValue(name, '');
        }
    };

    useEffect(() => {
        if (!Utils.isEmptyArray(fileUploadList) && isUploadForEmailTicket && props.setErrorCreateTicket) {
            let totalFileSize = 0;
            fileUploadList.forEach(file => {
                if (file?.file?.size) {
                    totalFileSize += parseInt(file?.file?.size);
                }
            })
            if (totalFileSize) {
                let convertTotalFileSizeToMB = Utils.convertByteToMB(totalFileSize)
                if (convertTotalFileSizeToMB > Constant.LIMIT_VALUE_VALIDATE.MB_FILE_UPLOAD_FOR_EMAIL_TICKET) {
                    props.setErrorCreateTicket(
                        name,
                        { type: 'manual', message: trI18n('MGS-FILE-OVER-CAPACITY').replace('{{fileSize}}', Constant.LIMIT_VALUE_VALIDATE.MB_FILE_UPLOAD_FOR_EMAIL_TICKET) },
                        { shouldFocus: true }
                    );
                }
            } else {
                props.clearErrors(name); 
            }
        } else {
            props.clearErrors(name);
        }
    }, [fileUploadList]);

    const handleDeleteFile = (index) => {
        setOpenOption(null);
        setOpenFileOption(null);
        let newFileUploadList = cloneDeep(fileUploadList);
        newFileUploadList.splice(index, 1);
        setValue(Constant.FORM_FILE_UPLOAD_LIST_INPUT_NAME, newFileUploadList);
        setFileUploadList([...newFileUploadList]);

        if(!Utils.isEmptyArray(newFileUploadList)) {
            setValue(name, 'fileUploaded');
            props.clearErrors(name);
        } else {
            setValue(name, '');
        }
    };

    // CR-2026-006: whether a file is a translatable type (pdf/docx/pptx/xlsx).
    const isTranslatableFile = (fileName) => {
        if (Utils.isEmptyString(fileName)) return false;
        const ext = fileName.split('.').pop().toLowerCase();
        return TranslationConstant.TRANSLATION_SUPPORTED_EXTENSIONS.includes(ext);
    };

    // CR-2026-006: whether the per-file auto-translate toggle should be shown.
    // Requires the format to have auto-translate ON, org gate ON, and a translatable file type.
    const isDisplayAutoTranslateTarget = (fileName) => {
        return (
            Boolean(props?.autoTranslateAttachment) &&
            Boolean(props?.isDisplayAutoTranslateSwitch) &&
            isTranslatableFile(fileName)
        );
    };

    // CR-2026-006: toggle per-file autoTranslate flag in fileUploadList.
    const onChangeAutoTranslate = (index) => {
        setFileUploadList(prev => {
            const prevClone = cloneDeep(prev);
            prevClone[index].autoTranslate = !prev[index].autoTranslate;
            setValue(Constant.FORM_FILE_UPLOAD_LIST_INPUT_NAME, prevClone);
            return prevClone;
        });
    };

    const handleToggleAutoTranslateTarget = () => {
        if (!openFileOption?.canEditAutoTranslate || isDisable) return;
        onChangeAutoTranslate(openFileOption.index);
        setOpenFileOption(prev => ({
            ...prev,
            autoTranslate: !prev?.autoTranslate
        }));
    };

    const renderImageUploadItem = (fileUploadInfor, item, url, index) => {
        return (
            <AttachFileThumbnail
                fileName={item.content}
                fileSize={item.fileSize}
                fileKey={url}
                imageUrl={url}
                size={thumbnailSize}
                onClickFile={() => setOpenPreviewImage(url)}
                onClickMore={e => {
                    setOpenFileOption({ ...item, index, isNewImg: true });
                    setOpenOption(e.currentTarget);
                }}
            />
        );
    };

    const renderOtherUploadItem = (fileUploadInfor, item, index) => {
        const fileName = fileUploadInfor.file.name;

        return (
            <AttachFileThumbnail
                fileName={item.content}
                fileSize={item.fileSize}
                size={thumbnailSize}
                onClickMore={e => {
                    setOpenFileOption({
                        ...item,
                        index,
                        isNewImg: true,
                        // CR-2026-006: per-file auto-translate flag
                        autoTranslate: Boolean(fileUploadInfor?.autoTranslate),
                        canEditAutoTranslate: isDisplayAutoTranslateTarget(fileName)
                    });
                    setOpenOption(e.currentTarget);
                }}
            />
        );
    };

    const renderImage = () => {
        let contentChatList = [];
        if (!isDisable) {
            fileUploadList.forEach((fileUploadInfor, index) => {
                let item = { content: fileUploadInfor.file.name, fileSize: fileUploadInfor.file.size };
                const url = URL.createObjectURL(fileUploadInfor.file);
                contentChatList.push(
                    <li key={fileUploadInfor.key || fileUploadInfor.file.name || index} style={{ marginBottom: 0 }}>
                        {Utils.isImageFile(fileUploadInfor.file.name)
                            ? renderImageUploadItem(fileUploadInfor, item, url, index)
                            : renderOtherUploadItem(fileUploadInfor, item, index)
                        }
                    </li>
                );
            });
        }
        return contentChatList;
    };

    const getFileUploadListStyle = () => {
        if (isDisable || Utils.isEmptyArray(fileUploadList)) return {};

        return {
            display: 'flex',
            columnGap: 10,
            rowGap: 10,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            marginBottom: 10
        };
    };

    const isCustomerHasFileUpload = (contentChatList) => {
        if (!Utils.isEmpty(props.addTicket)) {
            return false;
        }

        return props.componentName !== Constant.COMPONENT_NAME.DETAIL_TICKET_KNOWLEDGE && 
        publicForCustomer !== Constant.SETTING_PUBLIC_FOR_CUSTOMER.EDITABLE && 
        Utils.isEmptyArray(contentChatList)
    }

    const isCustomerKnowledgeHasFileUpload = (contentChatList) => {
        if (!Utils.isEmpty(props.addTicket)) {
            return false;
        }

        return props.componentName === Constant.COMPONENT_NAME.DETAIL_TICKET_KNOWLEDGE && 
        !props?.hasEditFieldPermissions && 
        Utils.isEmptyArray(contentChatList)
    }

    const renderLabel = () => {
        const contentChatList = renderImage();
        if (isCustomerHasFileUpload(contentChatList) || isCustomerKnowledgeHasFileUpload(contentChatList)) {
            return '';
        }

        return (
            <>
                {props?.isTooltipTitle ? (
                    <div className="d-flex">
                        <Tooltip title={props?.label}>
                            <span className="form-addTicket-label text-truncate">
                                <label
                                    className={
                                        'text-truncate line-height-18' +
                                        (publicForCustomer
                                            ? 'form-addTicket-label-color'
                                            : 'form-addTicket-label')
                                    }
                                >
                                    {props?.label}
                                </label>
                            </span>
                        </Tooltip>
                        {props.data.structure.required && (
                            <span className="required padding-left-4">*</span>
                        )}
                    </div>
                ) : (
                    <label
                        className={
                            publicForCustomer
                                ? 'form-addTicket-label-color'
                                : 'form-addTicket-label'
                        }
                    >
                        {props?.label}
                        {props.data.structure.required && <span className="required">*</span>}
                    </label>
                )}
                {props?.isShowIconCopyFieldId && (
                    <Tooltip title={props?.data.id} placement="top" arrow>
                        <img
                            style={{ paddingTop: '10px' }}
                            src="/assets/image/icon-copy.svg"
                            alt="copy"
                            onClick={() => navigator.clipboard.writeText(props?.data.id)}
                        />
                    </Tooltip>
                )}
            </>
        );
    }

    const checkDisableField = () => {
        return (props.data.structure.publicForCustomer === 2 || !Utils.isEmptyString(props.detailTicket?.deletedAt) || props.hasEditFieldPermissions === false );
    }

    const isDisplayBoxUploadFile = () => {
        if (!Utils.isEmpty(props.addTicket)) {
            return true;
        }

        if (props.componentName === Constant.COMPONENT_NAME.DETAIL_TICKET_KNOWLEDGE) {
            return props?.hasEditFieldPermissions;
        }

        return publicForCustomer === Constant.SETTING_PUBLIC_FOR_CUSTOMER.EDITABLE;
    }

    return (
        <div className="form-addTicket">
            <div className="cell-prod-status gap-8">{renderLabel()}</div>

            <div className="form-addTicket-input">
                <div className="lstName-file">
                    <ul style={getFileUploadListStyle()}>{renderImage()}</ul>
                </div>

                {isDisplayBoxUploadFile() && (
                    <div
                        className="formUpload"
                        {...getRootProps()}
                        data-cy="drop-file-input-upload"
                    >
                        <input disabled={isDisable} {...getInputProps()} draggable="false" />
                        <label htmlFor="contained-button-file">
                            <Button disabled={isDisable} variant="contained" component="span">
                                <div className="formUpload-item">
                                    <img
                                        src="/assets/image/icon-upload.svg"
                                        alt=""
                                        onDragStart={e => {
                                            e.preventDefault();
                                        }}
                                    />
                                    <span className="formUpload-txt">
                                        {trI18n('MSG-DRAG-DROP-FILE')}
                                    </span>
                                </div>
                            </Button>
                        </label>
                    </div>
                )}
                {props?.errorlist && props.errorlist[name]?.message && (
                    <div className="upload-file-error">{props.errorlist[name]?.message}</div>
                )}
            </div>

            <Popover
                open={Boolean(openOption) || false}
                anchorEl={openOption}
                onClose={() => {
                    setOpenFileOption(null);
                    setOpenOption(null);
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* CR-2026-006: per-file auto-translate toggle (自動翻訳対象) */}
                    {openFileOption?.canEditAutoTranslate && (
                        <ButtonOption
                            disabled={isDisable}
                            onClick={handleToggleAutoTranslateTarget}
                        >
                            <span
                                style={{
                                    fontSize: 15,
                                    fontWeight: 400,
                                    fontFamily: 'Zen Kaku Gothic New',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                }}
                            >
                                {trI18n('LABEL-ATTACHMENT-AUTO-TRANSLATE-TARGET')}
                                <div
                                    className="switch-check"
                                    onClick={e => e.stopPropagation()}
                                    style={{ marginLeft: 8 }}
                                >
                                    <input
                                        id="drop_file_option_auto_translate"
                                        type="checkbox"
                                        checked={Boolean(openFileOption?.autoTranslate)}
                                        disabled={isDisable}
                                        onChange={handleToggleAutoTranslateTarget}
                                    />
                                    <label htmlFor="drop_file_option_auto_translate"></label>
                                </div>
                            </span>
                        </ButtonOption>
                    )}
                    {!Utils.isEmptyObject(openFileOption) &&
                        !openFileOption?.originId?.includes('post') && (
                            <ButtonOption
                                disabled={isDisable}
                                onClick={() => {
                                    handleDeleteFile(openFileOption?.index);
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        fontFamily: 'Zen Kaku Gothic New'
                                    }}
                                >
                                    {trI18n('DELETE')}
                                </span>
                            </ButtonOption>
                        )}
                </div>
            </Popover>

            <Dialog
                open={!!openPreviewImage}
                onClose={() => setOpenPreviewImage(null)}
                maxWidth="md"
            >
                <img
                    alt=""
                    src={openPreviewImage}
                    style={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        objectFit: 'contain'
                    }}
                />
            </Dialog>
        </div>
    );
};

DropFile.propTypes = {
    data: PropTypes.any,
    detailTicket: PropTypes.any,
    hasEditFieldPermissions: PropTypes.any,
    selectTicketType: PropTypes.any,
    ticketType: PropTypes.any,
    errorlist: PropTypes.any,
    componentName: PropTypes.any,
    addTicket: PropTypes.any,
    fileUploadList: PropTypes.any,
    setFileUploadList: PropTypes.any,
    setValue: PropTypes.any,
    register: PropTypes.any,
    name: PropTypes.any,
    // CR-2026-006: attachment auto-translation config
    autoTranslateAttachment: PropTypes.bool,
    isDisplayAutoTranslateSwitch: PropTypes.bool
};
